import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Mail, Lock, ArrowLeft, Loader2, User, Phone, Globe, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../services/api';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const redirectUrl = searchParams.get('redirect') || '/dashboard';
  
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [nationality, setNationality] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Please enter your email'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email });
      setForgotSent(true);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Please fill in email and password'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(email, password, { first_name: firstName, last_name: lastName, phone, nationality, referral_code: sessionStorage.getItem('referral_code') || '' });
        sessionStorage.removeItem('referral_code');
        toast.success('Account created successfully!');
      }
      navigate(redirectUrl);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-teal-50 dark:from-[#161b22] dark:via-[#1c2128] dark:to-[#161b22] flex" data-testid="auth-page">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-24 py-12 overflow-y-auto">
        <Link to="/" className="flex items-center gap-2 text-teal-700 hover:opacity-80 transition-opacity mb-8" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo.jpg" alt="Triple A Travel Solutions" className="w-12 h-12 rounded-lg object-contain" />
            <div>
              <span className="font-serif text-xl font-bold text-teal-800 block leading-tight">Triple A</span>
              <span className="text-[10px] font-sans font-medium tracking-widest uppercase text-teal-600">Travel Solutions</span>
            </div>
          </div>

          <h1 className="font-serif text-3xl text-gray-900 mb-2">
            {mode === 'forgot' ? 'Reset password' : mode === 'login' ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="text-gray-500 mb-6">
            {mode === 'forgot' ? 'Enter your email and we\'ll send you a reset link' : mode === 'login' ? 'Sign in to access your saved itineraries' : 'Start planning your perfect adventure'}
          </p>

          {mode === 'forgot' ? (
            forgotSent ? (
              <div className="text-center py-4" data-testid="forgot-password-sent">
                <CheckCircle className="w-12 h-12 text-teal-600 mx-auto mb-3" />
                <p className="text-gray-700 font-medium mb-1">Check your email</p>
                <p className="text-gray-500 text-sm mb-4">If an account exists for <strong>{email}</strong>, you'll receive a reset link shortly.</p>
                <button type="button" onClick={() => { setMode('login'); setForgotSent(false); }}
                  className="text-teal-700 font-medium hover:underline text-sm" data-testid="back-to-login-after-sent">
                  Back to Sign In
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="email" className="text-gray-700 text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input id="email" type="email" placeholder="your@email.com" value={email}
                      onChange={(e) => setEmail(e.target.value)} className="pl-9 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20" data-testid="forgot-email-input" />
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 rounded-full font-semibold"
                  disabled={loading || !email} data-testid="forgot-submit-btn">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Link'}
                </Button>
                <p className="text-center">
                  <button type="button" onClick={() => setMode('login')}
                    className="text-teal-700 font-medium hover:underline text-sm" data-testid="back-to-login-from-forgot">
                    Back to Sign In
                  </button>
                </p>
              </form>
            )
          ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="firstName" className="text-gray-700 text-sm">First Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input id="firstName" placeholder="First name" value={firstName}
                        onChange={(e) => setFirstName(e.target.value)} className="pl-9 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20" data-testid="first-name-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="lastName" className="text-gray-700 text-sm">Last Name</Label>
                    <Input id="lastName" placeholder="Last name" value={lastName}
                      onChange={(e) => setLastName(e.target.value)} className="h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20" data-testid="last-name-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="phone" className="text-gray-700 text-sm">Phone Number <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input id="phone" placeholder="+61 400 000 000" value={phone}
                        onChange={(e) => setPhone(e.target.value)} className="pl-9 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20" data-testid="phone-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="nationality" className="text-gray-700 text-sm">Nationality <span className="text-gray-400 font-normal">(optional)</span></Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input id="nationality" placeholder="e.g., Australian" value={nationality}
                        onChange={(e) => setNationality(e.target.value)} className="pl-9 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20" data-testid="nationality-input" />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="space-y-1">
              <Label htmlFor="email" className="text-gray-700 text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input id="email" type="email" placeholder="your@email.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} className="pl-9 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20" data-testid="email-input" />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-gray-700 text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input id="password" type={showPassword ? "text" : "password"} placeholder="Min. 6 characters" value={password}
                  onChange={(e) => setPassword(e.target.value)} className="pl-9 pr-10 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500/20" data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="toggle-password-visibility" tabIndex={-1}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === 'login' && (
                <div className="flex justify-end pt-1">
                  <button type="button" onClick={() => setMode('forgot')}
                    className="text-xs text-teal-600 hover:text-teal-800 hover:underline transition-colors"
                    data-testid="forgot-password-link">
                    Forgot password?
                  </button>
                </div>
              )}
            </div>

            <Button type="submit" className="w-full h-11 bg-gradient-to-r from-teal-600 to-teal-700 text-white hover:from-teal-700 hover:to-teal-800 rounded-full font-semibold" disabled={loading} data-testid="submit-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : mode === 'login' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Google Sign-In Button */}
          <Button
            type="button"
            variant="outline"
            onClick={loginWithGoogle}
            className="w-full h-11 rounded-full border-gray-300 hover:bg-gray-50 font-medium text-gray-700 gap-2.5"
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-gray-500 mt-5">
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <button type="button" onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              className="text-teal-700 font-medium hover:underline" data-testid="toggle-mode-btn">
              {mode === 'login' ? 'Sign up' : 'Sign in'}
            </button>
          </p>
          </>
          )}
        </motion.div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative">
        <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80" alt="Beach paradise" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/40 to-amber-900/20" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white text-lg font-serif italic">"The world is a book, and those who do not travel read only one page."</blockquote>
          <p className="text-white/70 mt-2">-- Saint Augustine</p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
